import { Component, OnDestroy, OnInit } from '@angular/core';
import { CurrentService } from '../../services/current.service';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { CustomerService } from 'src/app/services/customer.service';
import { PrintService } from 'src/app/services/print.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { ProjectService } from 'src/app/services/project.service';
import { TunnelService } from 'src/app/services/tunnel.service';
import { KitchenService } from 'src/app/services/configurators-2d/applications/kitchen.service';
import { DatasService } from 'src/app/services/datas.service';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { StatisticService } from 'src/app/services/statistic.service';
import { Title } from '@angular/platform-browser';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';

import { KitchenPricing } from 'src/app/models/configurators-2d/applications/kitchen/kitchen-configuration-price';
import { Subscription } from 'rxjs';
import { InstallationInput } from '../../models/configurators-2d/tunnel/kitchen/kitchen-tunnel-store-model';
import { logError } from 'src/app/services/helpers/sentry.helper';

@Component({
  selector: 'ng-casto-summary',
  templateUrl: './summary.component.html',
  host: { class: 'container' },
})
export class SummaryComponent implements OnInit, OnDestroy {
  public beautifiedBom: any;

  public customerInfos: any;
  public kitchenPricing: KitchenPricing;
  public projectName: string;
  public isLess2Years: boolean = null;
  public installationError: string;
  /**
   Pourquoi Oui = false et Non = true ? Au début la question était "logement moins de 2 ans", donc Oui = true et Non = false.
   Maintenant c'est "logement plus de 2 ans". Donc pour limiter les modifications, j'inverse la valeur des réponses (logement plus de 2 ans = logement pas moins de 2 ans)
   */
  public isLess2YearsValues: any[] = [
    { label: 'Oui', value: false },
    { label: 'Non', value: true },
  ];
  public DiscountValue: DiscountTypeEnum = null;

  public DiscountValues: any[] = [
    {
      label: '5%',
      value: DiscountTypeEnum.Discount5,
      valueDiscount: 0.05,
      discountAmountWithInstallation: 0,
      discountAmountWithoutInstallation: 0,
    },
    {
      label: '10%',
      value: DiscountTypeEnum.Discount10,
      valueDiscount: 0.1,
      discountAmountWithInstallation: 0,
      discountAmountWithoutInstallation: 0,
    },
    {
      label: '20%',
      value: DiscountTypeEnum.Discount20,
      valueDiscount: 0.2,
      discountAmountWithInstallation: 0,
      discountAmountWithoutInstallation: 0,
    },
  ];

  public vendorEnvironment: boolean;
  public projectIsSaved: boolean = false;
  public printLoading: boolean;
  public installationDetails: any;
  public projectImageUrl: string = 'assets/img/placeholders/project@2x.png';

  public storeId: any;
  public currentVendor: any;
  public projectRef: any;
  public clientName: any;
  public messages: any;
  public warningDialogVisible: any = false;
  public isDataLoading: boolean;
  private _dataSubscription: Subscription;

  DISCOUNT_VALUES = DiscountTypeEnum;

  constructor(
    public modalService: ModalService,
    public customerService: CustomerService,
    public current: CurrentService,
    public projectService: ProjectService,
    public tunnelService: TunnelService,
    private readonly print: PrintService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly kitchenService: KitchenService,
    private readonly statisticService: StatisticService,
    public datasService: DatasService,
    private readonly titleService: Title
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  loadBeautifiedBom() {
    return this.tunnelService.kitchenTunnelData.bom;
  }

  get isDiscount() {
    return this.DiscountValue && this.DiscountValue !== this.DISCOUNT_VALUES.Discount0;
  }

  /**
   * Obtient le prix de livraison avec installation, en appliquant la réduction si disponible.
   */
  get shippingPrice() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;
    return this.kitchenPricing.withInstallation[this.priceKey].prixLivraison.discountPrices[discountValue].amount;
  }

  /**
   * Obtient le prix de livraison sans installation, avec réduction si applicable.
   */
  get shippingPricewithoutInstallation() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;
    return this.kitchenPricing.withoutInstallation.prixLivraison.discountPrices[discountValue].amount;
  }

  /**
   * Prix de l'option Workbench 3D sans remise.
   */
  get workbench3DPrice() {
    return this.kitchenPricing.workbenchInstallation.workbench3DPrice.amount ?? 0;
  }

  /**
   * Prix de l'option Workbench 3D avec remise.
   */
  get workbench3DPriceDiscount() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;
    return this.kitchenPricing.workbenchInstallation.workbench3DPrice.discountPrices[discountValue].amount ?? 0;
  }

  /**
   * Détermine la clé de prix en fonction de l'âge.
   */
  get priceKey() {
    return this.isLess2Years ? 'moins2' : 'plus2';
  }

  /**
   * Retourne les prix des catégories de produits sans installation.
   */
  get totalByCategories() {
    const input = this.kitchenPricing?.withoutInstallation.totalByCategories;
    return Object.keys(input).map(key => ({
      key,
      price: input[key].amount,
    }));
  }

  /**
   * Retourne les prix des catégories de produits sans installation.
   */
  productPricewithoutInstallationbyCategory(category: string) {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;

    return this.kitchenPricing.withoutInstallation.totalByCategories[category].discountPrices[discountValue].amount;
  }

  /**
   * Vérifie si le plan de travail est personnalisé.
   */
  get isCustomWorkbench() {
    return this.tunnelService.workbenchTunnelData.resume !== undefined;
  }

  /**
   * Retourne le prix du plan de travail.
   */
  get workbenchProductPrice() {
    return this.kitchenPricing.workbenchInstallation?.totalPrixPlanWorkbench.amount ?? 0;
  }

  /**
   * Retourne le prix total du plan de travail.
   */
  get workbenchTotalPrice() {
    return this.kitchenPricing.workbenchInstallation?.totalPrixWorkbench.amount ?? 0;
  }

  /**
   * Retourne le prix total du plan de travail avec la remise.
   */
  get workbenchTotalPriceDiscount() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;

    return this.kitchenPricing.workbenchInstallation?.totalPrixWorkbench.discountPrices[discountValue].amount ?? 0;
  }

  /**
   * Retourne le prix des packages de plan de travail.
   */
  get workbenchPackagePrice() {
    return this.kitchenPricing.workbenchInstallation.totalPrixPackagesPricesWorkbench.amount ?? 0;
  }

  /**
   * Retourne le prix du package de transport de plan de travail sans installation.
   */
  get workbenchPackageWithoutInstallationPrice() {
    return this.kitchenPricing?.workbenchInstallation?.packagesPrices?.Transport?.amount ?? 0;
  }

  /**
   * Retourne le prix de l'installation du plan de travail.
   */
  get workbenchInstallationPrice() {
    return this.kitchenPricing?.workbenchInstallation?.packagesPrices?.Installation?.amount ?? 0;
  }

  /**
   * Retourne le prix de l'installation du plan de travail.
   */
  get workbenchTransportPrice() {
    return this.kitchenPricing?.workbenchInstallation?.packagesPrices?.Transport?.amount ?? 0;
  }

  /**
   * Retourne le prix du plan de travail avec réduction.
   */
  get workbenchProductPriceDiscount() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;
    return this.kitchenPricing?.workbenchInstallation?.totalPrixPlanWorkbench.discountPrices[discountValue].amount ?? 0;
  }

  /**
   * Retourne le prix total des produits sans installation.
   */
  get totalProductPriceWithoutInstallation() {
    return this.kitchenPricing.withInstallation.moins2.sousTotalPrixProduit.amount;
  }

  /**
   * Retourne le prix des produits avec TVA à 10%.
   */
  get totalproductPrice10() {
    return this.kitchenPricing.withInstallation[this.priceKey].prixProduit.TVA10.amount;
  }

  /**
   * Retourne le prix des produits avec TVA à 10% avec réduction.
   */
  get totalProductPrice10Discount() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;

    return this.kitchenPricing.withInstallation[this.priceKey].prixProduit.TVA10.discountPrices[discountValue].amount;
  }

  /**
   * Retourne le prix des produits avec TVA à 20%.
   */
  get totalProductPrice20() {
    return this.kitchenPricing.withInstallation[this.priceKey].prixProduit.TVA20.amount;
  }

  /**
   * Retourne le prix des produits avec TVA à 20% avec réduction.
   */
  get totalProductPrice20Discount() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;

    return this.kitchenPricing.withInstallation[this.priceKey].prixProduit.TVA20.discountPrices[discountValue].amount;
  }

  /**
   * Retourne le prix des produits sans workbench.
   */
  get productPrice() {
    return this.kitchenPricing.withInstallation[this.priceKey].totalPrixProduit.amount;
  }

  /**
   * Retourne le prix des produits avec installation et réduction.
   */
  get productPriceDiscount() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;
    return this.kitchenPricing.withInstallation[this.priceKey].totalPrixProduit.discountPrices[discountValue].amount;
  }

  /**
   * Retourne le prix des produits avec workbench.
   */
  get totalProductPrice() {
    return this.kitchenPricing.withInstallation[this.priceKey].sousTotalPrixProduit.amount;
  }

  /**
   * Retourne le prix des produits avec workbench avec remise.
   */
  get totalProductPriceDiscount() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;

    return this.kitchenPricing.withInstallation[this.priceKey].sousTotalPrixProduit.discountPrices[discountValue]
      .amount;
  }

  /**
   * Retourne le prix total de l'installation.
   */
  get installationPrice() {
    return this.kitchenPricing?.withInstallation[this.priceKey].sousTotalPrixPose.amount;
  }

  /**
   * Retourne le prix total sans installation.
   */
  get totalWithoutInstallation() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;
    return this.kitchenPricing.withoutInstallation.prixTotal.discountPrices[discountValue].amount;
  }

  /**
   * Retourne le prix total avec installation et réduction.
   */
  get totalWithInstallation() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;
    return this.kitchenPricing.withInstallation[this.priceKey].prixTotal.discountPrices[discountValue].amount;
  }

  /**
   * Retourne le prix de installation avec TVA à 10%.
   */
  get installationProductPrice() {
    return this.kitchenPricing.withInstallation[this.priceKey].prixPose.TVA10.amount;
  }

  /**
   * Retourne le prix de installation avec TVA à 20%.
   */
  get installationAppliancePrice() {
    return this.kitchenPricing.withInstallation[this.priceKey].prixPose.TVA20.amount;
  }

  /**
   * Calcule la différence de prix après remise.
   */
  get delta() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;
    return this.kitchenPricing.withInstallation[this.priceKey].delta.discountPrices[discountValue].amount;
  }

  initFromBeautifiedBom() {
    this.beautifiedBom = this.loadBeautifiedBom();
  }

  ngOnInit(): void {
    this.warningDialogVisible = true;
    this.initFromBeautifiedBom();
    this.projectIsSaved = this.current.HBMProject;
    this.computeInstallationPrice(
      this.tunnelService.kitchenTunnelData.configId,
      this.tunnelService.workbenchTunnelData.resume?.products
    );

    if (!this.projectIsSaved) {
      return;
    }

    this.isLess2Years = this.tunnelService?.kitchenTunnelData?.isLess2Years ?? null;
    this.DiscountValue = this.tunnelService?.kitchenTunnelData?.discountValue ?? null;

    this.projectService.getDetails(this.current.HBMProject.uuid).then((response: any) => {
      this.projectRef = response.projectID;
      this.projectName = response.projectName;
      if (response.medias.thumbnail[1].url) {
        this.projectImageUrl = response.medias.thumbnail[1].url;
      }
    });

    this.customerInfos = {
      name: this.customerService.isLogged ? this.customerService.fullName : '(non identifié)',
      id: this.customerService.isLogged ? this.customerService.userDatas.id : '-',
    };
    this.customerService.context ??= CONFIG_CONTEXT.KITCHEN;

    this.vendorEnvironment = environment.vendor;

    this.currentVendor = this.current.vendor;
    this.storeId = this.currentVendor.store.SAPStoreId;
    this.clientName = this.customerService.fullName;

    this.messages = [
      {
        severity: 'warn',
        summary: 'Attention : ',
        detail:
          'Les électroménagers “GENERIQUES” ne sont pas pris en compte dans le chiffrage estimatif de la pose. Les réfrigérateurs à poser ne sont pas soumis à tarification.',
      },
    ];
  }

  subscribe() {
    this._dataSubscription = this.tunnelService.isDataLoading$.subscribe(isLoading => {
      this.isDataLoading = isLoading;
      if (!isLoading) {
        this.computeInstallationPrice(
          this.tunnelService.kitchenTunnelData.configId,
          this.tunnelService.workbenchTunnelData.resume?.products
        );
      }
    });
  }

  unsubscribe() {
    if (this._dataSubscription) this._dataSubscription.unsubscribe();
  }

  order() {
    if (this.isLess2Years === null) {
      return;
    }
    this.router.navigate(['/cuisine/vendor/financement']);
  }

  openDetail() {
    this.modalService.display(MODAL.INSTALLATION_DETAIL, {
      installationDetails: this.installationDetails,
      bom: this.beautifiedBom,
    });
  }

  openDetailWorkbench() {
    this.modalService.display(MODAL.WORKBENCH_DETAIL, {
      workbenchResume: this.kitchenPricing.workbenchInstallation.kitchenWorkbenchConfigurations,
      bom: this.beautifiedBom,
      discountValue: this.DiscountValue,
    });
  }

  async computeInstallationPrice(configId, workbenchConfigurationProducts) {
    this.isDataLoading = true;

    const input: InstallationInput = {
      configurationID: configId,
      workbenchConfigurationProducts: workbenchConfigurationProducts,
    };
    try {
      let response: KitchenPricing = await this.kitchenService.postInstallation(input);

      this.kitchenPricing = response;
      this.setInstallationData();
    } catch (error) {
      logError(`Erreur lors de la récupération des données de pose : ${error}`);
      this.installationError = error;
    } finally {
      this.isDataLoading = false;
    }
  }

  setInstallationData() {
    this.installationDetails = this.kitchenPricing.withInstallation[this.priceKey].elements;

    this.tunnelService.kitchenTunnelData.detailTable = this.installationDetails;

    this.storePrices();
    //this.updateDiscountAmounts();
  }

  //ça peut être utile un jour
  updateDiscountAmounts(): void {
    this.DiscountValues = this.DiscountValues.map(discount => ({
      ...discount,
      discountAmountWithInstallation:
        this.isLess2Years == undefined || this.isLess2Years == null
          ? 0
          : this.kitchenPricing.withInstallation[this.priceKey].prixTotal.discountPrices[discount.value].amountDiscount,
      discountAmountWithoutInstallation:
        this.kitchenPricing.withoutInstallation.prixTotal.discountPrices[discount.value].amountDiscount,
    }));
  }

  storePrices() {
    const discountValue = this.DiscountValue ?? DiscountTypeEnum.Discount0;

    this.tunnelService.kitchenTunnelData.installationPrice = this.installationPrice;
    this.tunnelService.kitchenTunnelData.appliancePrice =
      this.kitchenPricing.withInstallation[this.priceKey].prixProduitsElectromenager.discountPrices[
        discountValue
      ].amount;

    this.tunnelService.kitchenTunnelData.productPrice =
      this.kitchenPricing.withInstallation[this.priceKey].prixProduitsHorsElectromenager.discountPrices[
        discountValue
      ].amount;
    this.tunnelService.kitchenTunnelData.totalProductPrice = this.totalProductPriceDiscount;
    this.tunnelService.kitchenTunnelData.deliveryPrice = this.shippingPrice;
    this.tunnelService.kitchenTunnelData.workbenchProjectPriceDiscount = this.workbenchProductPriceDiscount;

    this.tunnelService.kitchenTunnelData.deliveryWithoutInstallationPrice =
      this.kitchenPricing.withoutInstallation.prixLivraison.discountPrices[discountValue].amount;

    this.tunnelService.kitchenTunnelData.totalProductPriceWithoutInstallation =
      this.kitchenPricing.withoutInstallation.sousTotalPrixProduit.discountPrices[discountValue].amount;
    this.tunnelService.kitchenTunnelData.appliancePriceWithoutInstallation =
      this.kitchenPricing.withoutInstallation.prixProduitsElectromenager.discountPrices[discountValue].amount;
    this.tunnelService.kitchenTunnelData.productPriceWithoutInstallation =
      this.kitchenPricing.withoutInstallation.prixProduitsHorsElectromenager.discountPrices[discountValue].amount;

    this.tunnelService.kitchenTunnelData.isLess2Years = this.isLess2Years;
    this.tunnelService.kitchenTunnelData.discountValue = this.DiscountValue;

    this.tunnelService.kitchenTunnelData.kitchenPricing = this.kitchenPricing;
  }

  back() {
    this.router.navigate(['/cuisine/vendor/configuration']); // on enleve la modal recap du prix de la pose pour aller sur une page dédiée
  }

  showProductList() {
    this.modalService.display(
      MODAL.PRODUCTS_LIST_V2,
      {
        bom: this.beautifiedBom,
        reloadBom: false,
        isSave: true,
        isReadOnly: this.current.HBMProject.locked,
      },
      false,
      () =>
        this.computeInstallationPrice(
          this.tunnelService.kitchenTunnelData.configId,
          this.tunnelService.workbenchTunnelData.resume?.products
        )
    );
  }

  printSummary() {
    this.printLoading = true;

    this.print.reset();
    this.print
      .addHeader(this.translate.instant('popins.installation.print_title_' + this.customerService.context))
      .then(() => {
        if (this.customerService.isLogged) {
          this.print.addText(
            this.translate.instant('popins.products_list.customer_id') + ' ' + this.customerInfos.id,
            'my-3'
          );
          this.print.addText(
            this.translate.instant('popins.products_list.name') + ' ' + this.customerInfos.name,
            'my-3'
          );
        }

        this.print.addText(
          this.translate.instant('popins.products_list.project') +
            ' ' +
            (this.current.HBMProject
              ? this.current.HBMProject.name
              : this.translate.instant('popins.price_info.placeholder_project_' + this.customerService.context)),
          'my-3'
        );

        let _rightNow = new Date();
        let _dateStr = _rightNow.toISOString().slice(0, 10).split('-');
        this.print.addText('Le ' + _dateStr[2] + '/' + _dateStr[1] + '/' + _dateStr[0], 'mt-3 mb-5');

        let _titleDiv = document.createElement('div');
        _titleDiv.className = 'installation-title';
        _titleDiv.innerHTML = this.translate.instant('popins.installation.title');
        this.print.addDiv(_titleDiv).then(() => {
          this.print.addDiv(document.querySelector('#product-prices').cloneNode(true) as HTMLDivElement).then(() => {
            let _installationPriceDiv = document
              .querySelector('#installation-prices')
              .cloneNode(true) as HTMLDivElement;

            _installationPriceDiv.querySelectorAll('.excludePrint').forEach(value => value.remove());
            _installationPriceDiv.querySelector('#details').setAttribute('style', 'display:block');
            _installationPriceDiv.className += 'mb-2';
            this.print.addDiv(_installationPriceDiv).then(() => {
              this.print.addDiv(document.querySelector('.print-legacy').cloneNode(true) as HTMLDivElement).then(() => {
                const currentTitle = this.titleService.getTitle();
                const newTitle = this.translate.instant('popins.print.print_title_kitchen');
                this.titleService.setTitle(newTitle);
                this.print.print();
                this.titleService.setTitle(currentTitle);

                this.printLoading = false;
              });
            });
          });
        });
      });
  }
}
